import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ShoppingBag,
  ArrowRight,
  Sparkles,
  Truck,
  ShieldCheck,
  Crown,
} from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { COLLECTIONS } from "../lib/products";
import { useCart } from "../lib/CartContext";

const accentMap = {
  amber: {
    text: "text-amber-400",
    bg: "bg-amber-400",
    bgHover: "hover:bg-amber-300",
    border: "border-amber-400/30",
    chip: "bg-amber-400 text-black",
  },
  gold: {
    text: "text-amber-300",
    bg: "bg-amber-300",
    bgHover: "hover:bg-amber-200",
    border: "border-amber-300/30",
    chip: "bg-amber-300 text-black",
  },
};

const ProductCard = ({ product, idx, total, accent }) => {
  const { addItem } = useCart();
  const handleAddToCart = () => {
    addItem(product);
    toast.success(`${product.name} added to cart`);
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: (idx % 4) * 0.08, duration: 0.6 }}
      data-testid={`product-${product.id}`}
      className="group relative border border-neutral-900 bg-[#0a0a0a] overflow-hidden flex flex-col"
    >
      <div className="relative aspect-square overflow-hidden bg-[#050505] border-b border-neutral-900 flex items-center justify-center p-2">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-contain group-hover:scale-[1.03] transition-transform duration-700"
        />
        <div
          className={`absolute top-3 left-3 ${accent.chip} text-[10px] uppercase tracking-[0.3em] font-bold px-2.5 py-1 z-10`}
        >
          {idx + 1} / {total}
        </div>
      </div>

      <div className="p-5 md:p-6 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-display uppercase text-xl md:text-2xl text-white leading-tight">
              {product.name}
            </h3>
            <div
              className={`text-[9px] uppercase tracking-[0.3em] ${accent.text} mt-2`}
            >
              {product.tagline}
            </div>
          </div>
          <div className="font-display text-2xl md:text-3xl text-white shrink-0">
            ${product.price}
          </div>
        </div>

        <p className="text-xs md:text-sm text-neutral-400 font-light leading-relaxed mt-4 flex-1">
          {product.description}
        </p>

        <div className="mt-5">
          <div className="text-[9px] uppercase tracking-[0.3em] text-neutral-500 mb-2">
            Includes
          </div>
          <ul className="space-y-1.5 text-xs text-neutral-300">
            {product.includes.map((item) => (
              <li key={item} className="flex items-start gap-2">
                <span className={`${accent.text} mt-1`}>▸</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-5 pt-4 border-t border-neutral-900">
          <div>
            <div className="text-[9px] uppercase tracking-[0.3em] text-neutral-500 mb-1.5">
              Sizes
            </div>
            <div className="flex flex-wrap gap-1.5">
              {product.sizes.slice(0, 5).map((s) => (
                <span
                  key={s}
                  className="border border-neutral-800 text-neutral-300 text-[10px] uppercase tracking-wider px-2 py-0.5"
                >
                  {s}
                </span>
              ))}
              {product.sizes.length > 5 && (
                <span className="text-[10px] text-neutral-500 px-1 py-0.5">
                  +{product.sizes.length - 5}
                </span>
              )}
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.3em] text-neutral-500 mb-1.5">
              Colorway
            </div>
            <div className="text-xs text-white">{product.colors[0]}</div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-5">
          <button
            onClick={handleAddToCart}
            data-testid={`add-to-cart-${product.id}`}
            className={`inline-flex items-center gap-2 ${accent.bg} text-black px-4 py-3 text-[10px] uppercase tracking-[0.25em] font-bold ${accent.bgHover} transition-colors flex-1 justify-center`}
          >
            <ShoppingBag size={12} />
            Add
          </button>
          <a
            href={`mailto:afroambasketball@gmail.com?subject=Order%20Inquiry%3A%20${encodeURIComponent(
              product.name
            )}`}
            data-testid={`inquire-${product.id}`}
            className="inline-flex items-center gap-2 border border-white text-white px-4 py-3 text-[10px] uppercase tracking-[0.25em] font-bold hover:bg-white hover:text-black transition-colors"
          >
            Inquire
            <ArrowRight size={12} />
          </a>
        </div>
      </div>
    </motion.article>
  );
};

const Collection = ({ collection }) => {
  const accent = accentMap[collection.accent] || accentMap.amber;
  return (
    <section
      id={collection.id}
      data-testid={`collection-${collection.id}`}
      className="border-b border-neutral-900 py-16 md:py-24"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-12">
          <div>
            <div
              className={`flex items-center gap-3 text-[10px] uppercase tracking-[0.45em] ${accent.text} mb-5`}
            >
              <span className={`w-8 h-px ${accent.bg.replace("bg-", "bg-")}`} />
              <Crown size={14} />
              <span>{collection.subtitle}</span>
            </div>
            <h2
              className="font-display uppercase text-4xl md:text-6xl lg:text-7xl leading-[0.9] text-white"
              data-testid={`collection-title-${collection.id}`}
            >
              {collection.name}
            </h2>
          </div>
          <p className="text-neutral-400 max-w-md font-light leading-relaxed">
            {collection.description}
          </p>
        </div>

        {/* Lookbook strip for collections that have one */}
        {collection.lookbook && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-12">
            {collection.lookbook.map((src, i) => (
              <motion.div
                key={src}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.7 }}
                className={`relative border ${accent.border} bg-[#050505] overflow-hidden flex items-center justify-center p-3 md:p-4`}
              >
                <img
                  src={src}
                  alt={`${collection.name} lookbook ${i + 1}`}
                  className="w-full h-auto max-h-[600px] object-contain"
                />
                <div className="absolute bottom-3 left-3 text-[9px] uppercase tracking-[0.3em] text-white bg-black/70 backdrop-blur px-2 py-1 z-10">
                  Lookbook · 0{i + 1}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Product grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
          {collection.products.map((p, i) => (
            <ProductCard
              key={p.id}
              product={p}
              idx={i}
              total={collection.products.length}
              accent={accent}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const StorePage = () => {
  return (
    <div className="App bg-black text-white min-h-screen">
      <Navbar />

      {/* Page Header */}
      <section className="relative pt-32 md:pt-40 pb-12 md:pb-16 border-b border-neutral-900 bg-gradient-to-b from-[#0a0a0a] to-black">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.45em] text-amber-400 mb-6">
            <span className="w-8 h-px bg-amber-400" />
            <ShoppingBag size={14} />
            <span>AfroAm Store · Collections</span>
          </div>
          <h1
            data-testid="store-title"
            className="font-display uppercase text-5xl md:text-7xl lg:text-8xl leading-[0.88] text-white"
          >
            Wear the
            <br />
            <span className="text-amber-400">movement.</span>
          </h1>
          <p className="text-neutral-400 max-w-xl mt-6 font-light leading-relaxed">
            One collection. One culture. Shop the official AfroAm Heritage
            drop — designed in the colors of two cultures, built with
            championship intent.
          </p>

          {/* Quick jump */}
          <div className="flex flex-wrap gap-3 mt-8">
            <a
              href="#afroam"
              data-testid="jump-afroam"
              className="border border-amber-400/40 hover:border-amber-400 text-amber-300 hover:text-amber-400 px-5 py-3 text-[10px] uppercase tracking-[0.28em] font-bold transition-colors"
            >
              AfroAm Heritage
            </a>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-b border-neutral-900 bg-black">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-neutral-900">
          {[
            { icon: Sparkles, label: "Limited Drops", sub: "Numbered & signed" },
            { icon: Truck, label: "Worldwide Shipping", sub: "From Houston, TX" },
            {
              icon: ShieldCheck,
              label: "Premium Build",
              sub: "Cotton blend + heritage trim",
            },
          ].map((f) => (
            <div
              key={f.label}
              className="py-6 px-2 md:px-8 flex items-center gap-4"
            >
              <f.icon size={18} className="text-amber-400 shrink-0" />
              <div>
                <div className="text-sm text-white uppercase tracking-wider">
                  {f.label}
                </div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                  {f.sub}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Collections */}
      {COLLECTIONS.map((c) => (
        <Collection key={c.id} collection={c} />
      ))}

      {/* Bottom CTA */}
      <section className="py-16 md:py-20 bg-black">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.35em] text-amber-400 mb-3">
              More drops coming soon
            </div>
            <h3 className="font-display uppercase text-3xl md:text-4xl text-white max-w-lg">
              Subscribe to be first in line for the next release.
            </h3>
          </div>
          <Link
            to="/#contact"
            data-testid="store-newsletter-cta"
            className="bg-white text-black px-7 py-4 text-xs uppercase tracking-[0.28em] font-bold hover:bg-neutral-200 transition-colors inline-flex items-center gap-3"
          >
            Get Notified
            <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StorePage;
